/**
 * Pure reducers for structure-mutating ops on the IR Document.
 *
 * Phase FB.3 of docs/design/frontend-mv-architecture-260524.md.
 * Phase FB.8: returns `{ir, dirtyPages}`. See §P.3.
 * Phase 4a (editor-pdf-item-editing-260910, Ph1b R2): the three structural
 * reducers are now structId-recursive for List items — reorder/create/delete
 * operate on an LI within a `List` block (not just top-level blocks) when the
 * op names a List parent. See §3.1.3.
 *
 * Layer: reducers.
 *
 * Reducers in this file:
 *   - reorderReadingReducer  — reorder-reading: top-level page blocks (no parentId),
 *                              OR a List's items when `op.parentId` is a List → `{page}`
 *   - setBboxReducer         — set-bbox (block.bbox)                   → `{block.page}`
 *   - createElementReducer   — create-element: append top-level block, OR insert a new
 *                              LI item when `parentStructId` is a List → `{page}`
 *   - deleteElementReducer   — delete-element: remove a top-level block OR a List item by
 *                              structId → `{deletedBlock.page}` or empty (idempotent)
 *
 * Every reducer is pure: input not mutated, output is a fresh Document
 * (wrapped in a ReducerResult).
 *
 * @module reducers/structure-reducers
 */
import { IRError, mutateBlocksById, findBlockById, buildBlockForRole, } from "../ir.js";
/**
 * Struct-tree ROOT sentinel — the path-form structId of the struct-tree root.
 * A top-level (whole-document) reorder / a create with no real parent carries
 * this (or, on the client, no parentId at all). Mirrors `STRUCT_TREE_ROOT_ID`
 * in `save-flow.ts` (the wire seam); duplicated here as a one-char defensive
 * guard rather than importing save-flow (which pulls fetch/log deps into this
 * pure, jsdom-free reducer seam).
 */
const STRUCT_TREE_ROOT = "S";
export function reorderReadingReducer(ir, op) {
    // Item reorder (Phase 4a): `op.parentId` names a List block → reorder that
    // List's items, where before/after are the LI structIds. Absent parentId
    // (the historical shape) or the struct-tree root sentinel → top-level page
    // reorder below.
    if (op.parentId !== undefined && op.parentId !== STRUCT_TREE_ROOT) {
        return reorderListItems(ir, op, op.parentId);
    }
    // Reorder blocks on the given page by the supplied id order.
    const pageBlocks = ir.blocks.filter((b) => b.page === op.pageNum);
    const pageIds = new Set(pageBlocks.map((b) => b.structId));
    const afterSet = new Set(op.after);
    // Validate: after must be exactly the set of ids on this page.
    for (const id of op.after) {
        if (!pageIds.has(id)) {
            throw new IRError("ReorderHasExtraIds", `structId ${id} is not on page ${op.pageNum}`);
        }
    }
    for (const id of pageIds) {
        if (!afterSet.has(id)) {
            throw new IRError("ReorderHasMissingIds", `structId ${id} on page ${op.pageNum} is missing from reorder`);
        }
    }
    const blockById = new Map(pageBlocks.map((b) => [b.structId, b]));
    const reordered = op.after.map((id) => blockById.get(id));
    // Splice the reordered page blocks back into the full list in their
    // original relative position.
    const result = [];
    let pi = 0;
    for (const b of ir.blocks) {
        if (b.page === op.pageNum) {
            result.push(reordered[pi++]); // WHY: reordered.length == pageBlocks.length; pi increments once per matching page block
        }
        else {
            result.push(b);
        }
    }
    return { ir: { ...ir, blocks: result }, dirtyPages: new Set([op.pageNum]) };
}
/**
 * Reorder the items of the `List` named by `parentId` to the LI-structId order
 * in `op.after`. Validation mirrors the top-level page reorder (extra / missing
 * id throws the same IRError codes) so the SPA gets a uniform failure surface.
 * A parentId that resolves to a non-List (or is not found) throws loudly
 * (A.14) — the caller addressed a reorder to something that has no reorderable
 * child order.
 */
function reorderListItems(ir, op, parentId) {
    const parent = findBlockById(ir.blocks, parentId);
    if (parent === null) {
        throw new IRError("ReorderParentNotFound", `reorder parent ${parentId} not found`);
    }
    if (parent.kind !== "List") {
        throw new IRError("ReorderParentNotList", `reorder parent ${parentId} is a ${parent.kind}, not a List`);
    }
    const itemById = new Map(parent.items.map((it) => [it.structId, it]));
    const afterSet = new Set(op.after);
    for (const id of op.after) {
        if (!itemById.has(id)) {
            throw new IRError("ReorderHasExtraIds", `structId ${id} is not an item of list ${parentId}`);
        }
    }
    for (const id of itemById.keys()) {
        if (!afterSet.has(id)) {
            throw new IRError("ReorderHasMissingIds", `list item ${id} of ${parentId} is missing from reorder`);
        }
    }
    const reorderedItems = op.after.map((id) => itemById.get(id));
    const blocks = mutateBlocksById(ir.blocks, parentId, (b) => {
        // b is the matched List; replace its items with the reordered set.
        if (b.kind !== "List")
            return b; // defensive — findBlockById already validated
        return { ...b, items: reorderedItems };
    });
    return { ir: { ...ir, blocks }, dirtyPages: new Set([parent.page]) };
}
export function setBboxReducer(ir, op) {
    // Update the bbox on a top-level block (and recurse into composite
    // blocks the same way mutateBlocksById does for set-* ops).
    // op.after is [x, y, w, h] in PDF user-space.
    if (!Array.isArray(op.after) || op.after.length !== 4) {
        throw new IRError("InvalidBbox", "set-bbox requires after = [x, y, w, h]");
    }
    let found = false;
    const blocks = mutateBlocksById(ir.blocks, op.structId, (b) => {
        found = true;
        return { ...b, bbox: { x: op.after[0], y: op.after[1], w: op.after[2], h: op.after[3] } };
    });
    if (!found)
        throw new IRError("StructIdNotFound", `structId ${op.structId} not found`);
    const next = { ...ir, blocks };
    const block = findBlockById(blocks, op.structId);
    return { ir: next, dirtyPages: new Set(block ? [block.page] : []) };
}
/**
 * Clamp a PDF-user-space bbox (points) to the page's MediaBox bounds so a
 * drawn-past-the-edge rect can never carry `x+w > pageWidth` or
 * `y+h > pageHeight` (and never negative x/y). Same shape as
 * `drag-move.ts:clampBboxToPage`, but that helper deliberately preserves
 * w/h (position-only drag); here w/h themselves may need shrinking because
 * a freshly-drawn create-element rect can originate with an out-of-page
 * SIZE, not just an out-of-page origin (root cause of the davis gt-5
 * w=1.409 GT-export overflow — clamping here means every downstream
 * PDF-points→[0,1] normalization, e.g. `pdfUserSpaceXywhToNorm`, is
 * mechanically bounded to [0,1]). No-op when page dims are unavailable.
 */
function clampCreateBboxToPage(bbox, page) {
    if (!page || !Number.isFinite(page.width) || !Number.isFinite(page.height))
        return bbox;
    const x = Math.min(Math.max(bbox.x, 0), page.width);
    const y = Math.min(Math.max(bbox.y, 0), page.height);
    const w = Math.min(Math.max(bbox.w, 0), page.width - x);
    const h = Math.min(Math.max(bbox.h, 0), page.height - y);
    return { x, y, w, h };
}
export function createElementReducer(ir, op) {
    // Append a new block at the document tail. Path-form structId is
    // client-generated by the caller (e.g. UUID or "editor-insert-N").
    // The wire op carries parentStructId so the server-side
    // restructurer (PdfStructTreeKidsMutator.AddChild) knows where to
    // attach; for the local reducer we just place the block at the
    // end of ir.blocks so the card list reflects it immediately.
    //
    // Role validation lives at the caller (the role is picked from a
    // closed enum — paragraph/H1-6/etc.) so the reducer doesn't fail
    // a valid restructurer-supplied role just because it isn't in the
    // legacy ROLES set.
    if (!op.structId) {
        throw new IRError("InvalidCreateElement", "structId is required");
    }
    if (!Array.isArray(op.bbox) || op.bbox.length !== 4) {
        throw new IRError("InvalidBbox", "create-element requires bbox = [x, y, w, h]");
    }
    const role = String(op.role || "P");
    const page = op.page || 1;
    const pageDims = ir.pages?.find((p) => p.number === page);
    const bbox = clampCreateBboxToPage({ x: op.bbox[0], y: op.bbox[1], w: op.bbox[2], h: op.bbox[3] }, pageDims);
    // Item insert (Phase 4a): when `parentStructId` resolves to a List block,
    // insert a new LI item into that List's `items` at `insertIndex` (append
    // when absent/out-of-range) rather than materializing a top-level card.
    // A parentStructId that is absent, the struct-tree root sentinel, or does
    // NOT resolve to a List falls through to the top-level append — preserving
    // the historical create-element behavior (which ignored parentStructId for
    // non-item inserts).
    if (op.parentStructId && op.parentStructId !== STRUCT_TREE_ROOT) {
        const parent = findBlockById(ir.blocks, op.parentStructId);
        if (parent !== null && parent.kind === "List") {
            return insertListItem(ir, op, parent);
        }
    }
    // Map the role onto a fresh IR block through the shared seam (the same one
    // coerceRole uses) so draw-create and role-change stay in lock-step. A
    // freshly-drawn block carries no source content, so the seam seeds empty
    // text; composite roles ("L"/"List"/"Table") build a VALID composite block
    // rather than silently downgrading to Paragraph (editor-list-rca).
    const block = buildBlockForRole(role, op.structId, page, { bbox }, {});
    return { ir: { ...ir, blocks: [...ir.blocks, block] }, dirtyPages: new Set([page]) };
}
/**
 * Insert a new (empty) LI item into `list`'s items at `op.insertIndex` (append
 * when absent or out of range). The fresh item's single content block is a
 * Paragraph carrying the LI's OWN structId (`op.structId`) — mirroring
 * `nodeToListItem` / `BuildListItem` — so a subsequent `set-actual-text` on
 * `op.structId` lands on the item through `mutateBlocksById` and the server
 * writes `/ActualText` on the LI elem (the item-edit round-trip, G-4 c). The
 * item lives on the List's page.
 */
function insertListItem(ir, op, list) {
    const page = list.page;
    const para = {
        kind: "Paragraph",
        structId: op.structId,
        content: [{ kind: "Text", value: "" }],
        page,
    };
    const newItem = { structId: op.structId, content: [para] };
    const idx = (typeof op.insertIndex === "number"
        && op.insertIndex >= 0
        && op.insertIndex <= list.items.length)
        ? op.insertIndex
        : list.items.length;
    const nextItems = [...list.items.slice(0, idx), newItem, ...list.items.slice(idx)];
    const blocks = mutateBlocksById(ir.blocks, list.structId, (b) => {
        if (b.kind !== "List")
            return b; // defensive
        return { ...b, items: nextItems };
    });
    return { ir: { ...ir, blocks }, dirtyPages: new Set([page]) };
}
export function deleteElementReducer(ir, op) {
    // Inverse-symmetric with create-element. Removes the top-level block whose
    // structId matches op.structId, OR — Phase 4a — an LI item within a List
    // when op.structId names an item (List items are NOT top-level blocks; a
    // List's own structId is distinct from any of its item structIds, so the
    // two branches never collide). Idempotent: if the target is already gone
    // (the user undid a re-applied delete, or two oplog entries collide on the
    // same id) the reducer is a no-op rather than a throw — matching the
    // server-side RemoveChild semantics where a stale id resolves to "not
    // found" and is non-fatal.
    //
    // Undo (per Ph1b R2 "delete-item … with undo restoring it") is built by the
    // caller as an inverse create-element (re-inserting the item); the reducer
    // itself only performs the removal.
    if (!op.structId) {
        throw new IRError("InvalidDeleteElement", "structId is required");
    }
    // Look up the deleted block's page BEFORE filtering — once the block is
    // out of ir.blocks the page is unrecoverable.
    const deletedBlock = ir.blocks.find((b) => b.structId === op.structId);
    if (deletedBlock !== undefined) {
        const filtered = ir.blocks.filter((b) => b.structId !== op.structId);
        return {
            ir: { ...ir, blocks: filtered },
            dirtyPages: new Set([deletedBlock.page]),
        };
    }
    // Not a top-level block — try removing it as a List item (Phase 4a).
    const itemRemoval = removeListItem(ir.blocks, op.structId);
    if (itemRemoval !== null) {
        return {
            ir: { ...ir, blocks: itemRemoval.blocks },
            dirtyPages: new Set([itemRemoval.page]),
        };
    }
    // Idempotent no-op (target already gone / unknown id): return the original
    // ir + an empty dirtyPages set so the dispatcher skips the rebuild + skips
    // subscriber notifications.
    return { ir, dirtyPages: new Set() };
}
/**
 * Remove the LI item whose structId matches `structId` from whichever `List`
 * block contains it. Returns the new block array + the List's page, or `null`
 * when no List holds such an item (the caller then treats the delete as an
 * idempotent no-op).
 */
function removeListItem(blocks, structId) {
    for (const b of blocks) {
        if (b.kind !== "List")
            continue;
        if (!b.items.some((it) => it.structId === structId))
            continue;
        const nextItems = b.items.filter((it) => it.structId !== structId);
        const nextBlocks = blocks.map((bb) => (bb === b ? { ...b, items: nextItems } : bb));
        return { blocks: nextBlocks, page: b.page };
    }
    return null;
}
